import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem
} from '@mui/material';
import {
  AssignmentOutlined,
  WarningAmberRounded,
  CheckCircleOutline,
  ArrowForwardRounded,
  EditNoteOutlined
} from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';

export interface NextStep {
  uid: string;
  title?: string;
  componentType?: string;
  instructionText?: string;
  /** Full step object (passed through to renderers that need component.fields etc.). */
  raw?: any;
}

interface Props {
  teamUid?: string;
  transactionUid?: string;
  /** Steps the viewer can currently act on (filtered by role outside). */
  steps?: NextStep[];
  /** Called after a successful submission so the parent can reload details. */
  onSubmitted?: () => void | Promise<void>;
  /** Called when the user picks an "Open <step>" affordance for non-inline step types. */
  onOpenStep?: (step: NextStep) => void;
}

/**
 * Sticky right-rail card. Branches by the first actionable next step's
 * `componentType`:
 *   - INSTRUCTION → instruction text + Accept and Continue CTA
 *   - FORM        → inline fields + Save Step submit
 *   - others      → Open <stepName> button → delegated to parent
 *
 * All submissions go through apiContext.uploadTransaction with the
 * standard event payload shape, mirroring how Form.tsx + EditForm do it.
 */
const NextStepsPanel: React.FC<Props> = ({
  teamUid,
  transactionUid,
  steps = [],
  onSubmitted,
  onOpenStep
}) => {
  const apiContext = useContext(ApiContext) as any;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const first = useMemo(() => steps.find((s) => s?.uid) || null, [steps]);

  const submit = async (payload: Record<string, any>) => {
    if (!apiContext?.uploadTransaction || !first) return;
    setSubmitting(true);
    setError(null);
    try {
      const fullPayload = {
        eventSource: 'WEB',
        eventType: payload.eventType,
        teamUid,
        key: transactionUid,
        displayName: transactionUid,
        step: first.uid,
        files: [],
        data: payload.data || [],
        ...payload.extra
      };
      const res = await apiContext.uploadTransaction(fullPayload);
      if (res && res.success === false) {
        setError(res.message || 'Failed to submit step');
        return;
      }
      await onSubmitted?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to submit step');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (!first) {
      return <EmptyState />;
    }

    const componentType = (first.componentType || '').toUpperCase();

    if (componentType === 'INSTRUCTION') {
      return <InstructionBody step={first} submitting={submitting} error={error} onAccept={() => submit({ eventType: 'INSTRUCTION', data: [] })} />;
    }
    if (componentType === 'FORM') {
      return <FormBody step={first} submitting={submitting} error={error} onSubmit={(data) => submit({ eventType: 'FORM', data })} />;
    }
    return <OpenStepButton step={first} onOpenStep={onOpenStep} />;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: { md: 'sticky' },
        top: { md: 12 },
        maxHeight: { md: 'calc(100vh - 24px)' },
        overflow: 'auto',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <AssignmentOutlined sx={{ color: 'primary.main', fontSize: 18 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Next Steps &amp; Forms
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>{renderBody()}</Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      py: 5
    }}
  >
    <AssignmentOutlined sx={{ color: 'grey.400', fontSize: 40 }} />
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ textAlign: 'center', fontSize: '0.82rem' }}
    >
      No next steps or form fields available for this transaction.
    </Typography>
  </Box>
);

const InstructionBody: React.FC<{
  step: NextStep;
  submitting: boolean;
  error: string | null;
  onAccept: () => void;
}> = ({ step, submitting, error, onAccept }) => {
  const text =
    step.instructionText ||
    step.raw?.component?.settings?.instructions ||
    step.raw?.component?.description ||
    'Continue only if the shipment status needs to be changed.';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'warning.light',
          border: '1px solid',
          borderColor: 'warning.main',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        <WarningAmberRounded sx={{ color: 'warning.dark', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
            {step.title || 'Instruction to change status'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {text}
          </Typography>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" onClose={() => {}} sx={{ fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}
      <Button
        variant="contained"
        color="primary"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
        onClick={onAccept}
        disabled={submitting}
        fullWidth
      >
        Accept and Continue
      </Button>
    </Box>
  );
};

const FormBody: React.FC<{
  step: NextStep;
  submitting: boolean;
  error: string | null;
  onSubmit: (data: any[]) => void;
}> = ({ step, submitting, error, onSubmit }) => {
  const fields = useMemo(() => {
    const f = step.raw?.component?.fields;
    return Array.isArray(f) ? f : [];
  }, [step.raw]);

  const [values, setValues] = useState<Record<string, any>>({});

  const setField = (fieldUid: string, value: any) =>
    setValues((prev) => ({ ...prev, [fieldUid]: value }));

  if (fields.length === 0) {
    // Form step with no field definitions — fall back to an Open button so the
    // user can use the existing EditForm modal.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {step.title ? `Open "${step.title}" to fill the form fields.` : 'Open the form to fill in fields.'}
        </Typography>
        <Button
          variant="contained"
          endIcon={<ArrowForwardRounded />}
          disabled={submitting}
          fullWidth
        >
          Open Form
        </Button>
      </Box>
    );
  }

  const handleSubmit = () => {
    const data = fields.map((f: any) => {
      const v = values[f.uid];
      return {
        question: f.question,
        description: f.description,
        inputComponent: f.inputComponent,
        field: f.uid,
        value: {
          value: Array.isArray(v) ? v : v != null ? [v] : [''],
          files: []
        }
      };
    });
    onSubmit(data);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditNoteOutlined sx={{ color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {step.title || 'Next step'}
        </Typography>
      </Box>
      {fields.map((f: any) => {
        const label = f.question || f.name || f.uid;
        const ic = (f.inputComponent || 'TEXT').toUpperCase();
        if (ic === 'SELECT' || ic === 'RADIO') {
          const options: any[] = Array.isArray(f.options) ? f.options : [];
          return (
            <TextField
              key={f.uid}
              size="small"
              select
              fullWidth
              label={label}
              value={values[f.uid] ?? ''}
              onChange={(e) => setField(f.uid, e.target.value)}
            >
              {options.map((o: any) => (
                <MenuItem key={o.value || o.label} value={o.value || o.label}>
                  {o.label || o.value}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        if (ic === 'NUMBER') {
          return (
            <TextField
              key={f.uid}
              size="small"
              type="number"
              fullWidth
              label={label}
              value={values[f.uid] ?? ''}
              onChange={(e) => setField(f.uid, e.target.value)}
            />
          );
        }
        // Default: text / textarea — keep it simple for v1.
        return (
          <TextField
            key={f.uid}
            size="small"
            fullWidth
            multiline={ic === 'TEXTAREA'}
            minRows={ic === 'TEXTAREA' ? 2 : 1}
            label={label}
            value={values[f.uid] ?? ''}
            onChange={(e) => setField(f.uid, e.target.value)}
          />
        );
      })}
      {error && (
        <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}
      <Button
        variant="contained"
        color="primary"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
        onClick={handleSubmit}
        disabled={submitting}
        fullWidth
      >
        Save Step
      </Button>
    </Box>
  );
};

const OpenStepButton: React.FC<{
  step: NextStep;
  onOpenStep?: (step: NextStep) => void;
}> = ({ step, onOpenStep }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Typography variant="body2" color="text.secondary">
      {step.title
        ? `Open "${step.title}" to complete this step.`
        : 'Open the next step to continue.'}
    </Typography>
    {step.componentType && (
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
        {step.componentType}
      </Typography>
    )}
    <Button
      variant="contained"
      endIcon={<ArrowForwardRounded />}
      onClick={() => onOpenStep?.(step)}
      fullWidth
    >
      Open {step.title || 'Step'}
    </Button>
  </Box>
);

export default NextStepsPanel;
